import { Link } from "react-router-dom";
import { Globe, TrendingUp, FileText, Users, ChevronRight } from "lucide-react";
import { Page, PageHero } from "../components/Layout";
import { FadeIn } from "../components/motion";
import { TEAM } from "../data/content";

export default function Team() {
  // Map icons to the directors based on their initials or roles
  const getDirectorConfig = (initials) => {
    switch (initials) {
      case "UL":
        return {
          icon: <FileText size={22} className="text-white" />,
          iconBg: "bg-[#2D2A4A]",
          textColor: "text-[#2D2A4A]",
          gradient: "from-[#3D3A6B] via-[#201E3D] to-[#100F21]",
          tag: "DIRECTOR - FINANCE & STRATEGIC PLANNING"
        };
      case "US":
        return {
          icon: <Globe size={22} className="text-white" />,
          iconBg: "bg-[#4A1136]",
          textColor: "text-[#4A1136]",
          gradient: "from-[#4A1136] via-[#2A0E20] to-[#1A0612]",
          tag: "DIRECTOR - OPERATIONS & GLOBAL SOURCING"
        };
      case "AS":
        return {
          icon: <TrendingUp size={22} className="text-white" />,
          iconBg: "bg-[#4A1136]",
          textColor: "text-[#4A1136]",
          gradient: "from-[#4A1136] via-[#2A0E20] to-[#1A0612]",
          tag: "DIRECTOR - BUSINESS DEVELOPMENT & GLOBAL GROWTH"
        };
      default:
        return {
          icon: <Users size={22} className="text-white" />,
          iconBg: "bg-[#4A1136]",
          textColor: "text-[#4A1136]",
          gradient: "from-[#4A1136] to-[#2A0E20]",
          tag: "DIRECTOR"
        };
    }
  };

  return (
    <Page testId="team-page">
      <PageHero
        image="/assets/bg-team.jpg"
        eyebrow="Leadership"
        title={<>Driven by a <span className="text-[#D4AF37]">forward-thinking</span> leadership team.</>}
        sub="A workforce that shares a common vision — creating long-term value for clients while making a positive impact in the lives of the individuals we place."
      />

      <section className="bg-white py-20 relative overflow-hidden" data-testid="team-section">
        {/* Subtle background decorative grid */}
        <div className="absolute inset-0 bg-[radial-gradient(#FAF7F2_1px,transparent_1px)] [background-size:16px_16px] opacity-70 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <FadeIn>
              <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#811F5D] mb-4">Board of Directors</p>
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="h-[1px] w-12 bg-[#D4AF37]" />
                <span className="w-2 h-2 rotate-45 bg-[#D4AF37]" />
                <div className="h-[1px] w-12 bg-[#D4AF37]" />
              </div>
            </FadeIn>
          </div>

          <div className="space-y-12 sm:space-y-16">
            {TEAM.map((t, idx) => {
              const isEven = idx % 2 === 0;
              const config = getDirectorConfig(t.initials);

              return (
                <FadeIn key={t.name}>
                  <div 
                    className={`flex flex-col-reverse bg-[#F7F5F0] rounded-[2rem] overflow-hidden border border-[#811F5D]/5 shadow-[0_15px_50px_rgba(74,17,54,0.04)] hover:shadow-[0_30px_70px_rgba(74,17,54,0.08)] transition-all duration-500 ${
                      isEven ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                    data-testid={`team-card-${t.name.toLowerCase().replaceAll(" ", "-")}`}
                  >
                    {/* Content Section */}
                    <div className="w-full md:w-[60%] p-6 sm:p-12 lg:p-16 flex flex-col justify-center relative">
                      <div className="flex items-center gap-4 mb-4 sm:mb-6">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl ${config.iconBg} flex items-center justify-center shadow-lg border border-white/10 shrink-0`}>
                          {config.icon}
                        </div>
                        <span className={`font-mono text-[10px] sm:text-xs tracking-wider uppercase ${config.textColor} font-bold`}>
                          {config.tag}
                        </span>
                      </div>
                      
                      <h3 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-4xl tracking-tight mb-3 sm:mb-4">
                        {t.name}
                      </h3>
                      <div className="w-12 h-[3px] bg-[#D4AF37] mb-4 sm:mb-6" />
                      
                      <p className="text-xs sm:text-base text-[#5C4D56] leading-relaxed font-medium">
                        {t.bio}
                      </p>
                    </div>

                    {/* Image Section */}
                    <div 
                      className="w-full md:w-[40%] min-h-[280px] sm:min-h-[350px] md:min-h-auto bg-white overflow-hidden flex items-end justify-center relative border-b-4 md:border-b-0 border-[#D4AF37]"
                    >

                      
                      {/* Parallel Slanted Gold Separator (Desktop Only) */}
                      {isEven ? (
                        <>
                          <div 
                            className="absolute top-0 bottom-0 left-0 w-full bg-[#D4AF37] pointer-events-none z-20 hidden md:block"
                            style={{ clipPath: 'polygon(15% 0%, 16.5% 0%, 1.5% 100%, 0% 100%)' }}
                          />
                        </>
                      ) : (
                        <>
                          <div 
                            className="absolute top-0 bottom-0 left-0 w-full bg-[#D4AF37] pointer-events-none z-20 hidden md:block"
                            style={{ clipPath: 'polygon(98.5% 0%, 100% 0%, 85% 100%, 83.5% 100%)' }}
                          />
                        </>
                      )}

                      {/* Actual image */}
                      <img 
                        src={t.image} 
                        alt={t.name} 
                        className={`h-[92%] w-auto object-contain object-bottom relative z-10 transition-transform duration-700 hover:scale-105 select-none ${
                          isEven ? "md:translate-x-4" : "md:-translate-x-4"
                        }`}
                      />
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>

          {/* Custom Bottom CTA Banner from Screenshot */}
          <div className="mt-16 sm:mt-24">
            <FadeIn>
              <Link
                to="/contact"
                className="group block bg-[#2A0E20] hover:bg-[#1E0916] rounded-3xl md:rounded-full p-6 md:px-10 md:py-6 border border-[#811F5D]/20 shadow-[0_20px_50px_rgba(42,14,32,0.15)] transition-all duration-300 active:scale-[0.99] max-w-5xl mx-auto"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  {/* Left Info */}
                  <div className="flex flex-col sm:flex-row items-center text-center sm:text-left gap-4 sm:gap-5">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#E5C158] to-[#B29235] flex items-center justify-center shadow-lg shrink-0">
                      <Users size={20} className="text-[#2A0E20]" />
                    </div>
                    <div>
                      <h4 className="font-heading font-extrabold text-white text-base sm:text-lg tracking-tight">
                        Experienced leadership. Global vision. Sustainable impact.
                      </h4>
                    </div>
                  </div>

                  {/* Middle Divider (Desktop only) */}
                  <div className="hidden md:block h-8 w-[1px] bg-white/10" />

                  {/* Right Text / Action */}
                  <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-right shrink-0">
                    <span className="text-xs sm:text-sm font-semibold text-white/80 group-hover:text-white transition-colors">
                      Driving excellence and building the future of global staffing.
                    </span>
                    <div className="w-8 h-8 rounded-full bg-white/5 group-hover:bg-[#D4AF37] flex items-center justify-center text-white group-hover:text-[#2A0E20] transition-all duration-300 shrink-0">
                      <ChevronRight size={18} />
                    </div>
                  </div>
                </div>
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>
    </Page>
  );
}
